// AI Translation Service that interfaces with the Supabase function to call OpenAI

import { createClient } from '@supabase/supabase-js';
import {
  AI_TRANSLATOR_PERSONA,
  BATCH_TRANSLATION_GUIDELINES,
  BATCH_TRANSLATION_OUTPUT_FORMAT,
  DEFAULT_PRODUCT_CONTEXT,
  DEFAULT_TONE_OF_VOICE,
  DETAILED_PLACEHOLDERS,
  PromptConfig,
  PromptSection,
  SINGLE_TRANSLATION_OUTPUT_FORMAT,
  TRANSLATION_GUIDELINES
} from './translationPrompts';

export enum TranslationModel {
  GPT4_MINI = 'gpt-4o-mini',
  GPT4 = 'gpt-4o',
}

export enum TranslationMode {
  ALL = 'all',
  MISSING = 'missing',
}

export interface TranslationItem {
  key: string;
  value: string;
}

// Interface for the OpenAI API request
interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface ChatQuery {
  messages: ChatMessage[];
  model: string;
  temperature?: number;
  responseFormat?: {
    type: string;
  };
}

// Interface for OpenAI API response
interface ChatResult {
  id: string;
  choices: {
    message: {
      content: string;
    };
  }[];
}

// Create a custom Supabase client with proper headers
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL and anon key must be set in environment variables.');
}
const translationSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
  },
});

class AITranslationService {
  private static instance: AITranslationService;
  private supabaseFunctionUrl = process.env.REACT_APP_SUPABASE_TRANSLATE_FUNCTION_URL;
  private supabaseApiKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  
  private sourceLanguage: string = 'en';
  private currentModel: TranslationModel = TranslationModel.GPT4_MINI;
  
  public static getInstance(): AITranslationService {
    if (!AITranslationService.instance) {
      AITranslationService.instance = new AITranslationService();
    }
    return AITranslationService.instance;
  }
  
  private constructor() {}
  
  public setSourceLanguage(languageCode: string): void {
    this.sourceLanguage = languageCode;
  }
  
  public setModel(model: TranslationModel): void {
    this.currentModel = model;
  }
  
  /**
   * Translate a single text string
   * @param text The text to translate
   * @param targetLanguage The target language code
   * @returns A promise that resolves to the translated text
   */
  public async translateText(text: string, targetLanguage: string): Promise<string> {
    if (!text) {
      throw new Error('Empty text provided for translation');
    }

    if (!targetLanguage) {
      throw new Error('No target language specified');
    }

    const systemPrompt = await this.getTranslationPrompt(this.sourceLanguage, targetLanguage);
    
    const query: ChatQuery = {
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: text }
      ],
      model: this.currentModel,
      temperature: 0
    };

    const response = await this.callOpenAI(query);
    return response;
  }
  
  /**
   * Translate an array of translation items
   * @param items Array of {key, value} pairs to translate
   * @param targetLanguage The target language code
   * @returns A promise that resolves to an array of translated {key, value} pairs
   */
  public async translateItems(items: TranslationItem[], targetLanguage: string): Promise<TranslationItem[]> {
    try {
      const prompt = await this.getBatchTranslationPrompt(this.sourceLanguage, targetLanguage);
      
      // Convert items to JSON string
      const itemsJson = JSON.stringify(items, null, 2);
      
      const query: ChatQuery = {
        messages: [
          { role: 'system', content: prompt },
          { role: 'user', content: itemsJson }
        ],
        model: this.currentModel,
        temperature: 0
      };
      
      const response = await this.callOpenAI(query);
      
      // Parse the JSON response
      try {
        return JSON.parse(response) as TranslationItem[];
      } catch (parseError) {
        // Error handling without logging
        throw new Error('Failed to parse AI translation response');
      }
    } catch (error) {
      // Error handling without logging
      throw error;
    }
  }
  
  /**
   * Call the OpenAI API through the Supabase function
   * @param query The chat query to send to OpenAI
   * @returns A promise that resolves to the AI response
   */
  private async callOpenAI(query: ChatQuery): Promise<string> {
    if (!this.supabaseFunctionUrl) {
      throw new Error('Supabase function URL must be set in environment variables.');
    }

    // Get the current user's access token
    const { data: { session } } = await translationSupabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      throw new Error('User is not authenticated.');
    }

    const response = await fetch(this.supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(query),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    if (!data || !data.choices?.[0]?.message?.content) {
      throw new Error('Invalid response format from AI service');
    }

    return data.choices[0].message.content.trim();
  }
  
  /**
   * Get the product context for translations
   * @param selectedTeamId The team ID to filter by
   * @returns The product context string
   */
  private async getProductContext(selectedTeamId?: string): Promise<string> {
    try {
      if (!selectedTeamId) return DEFAULT_PRODUCT_CONTEXT;
      // Try to fetch the product context from the active team
      const { data: teamData, error } = await translationSupabase
        .from('Teams')
        .select('tov_product_context')
        .eq('id', selectedTeamId)
        .single();
      if (error || !teamData?.tov_product_context) {
        return DEFAULT_PRODUCT_CONTEXT;
      }
      return teamData.tov_product_context;
    } catch (error) {
      // Fall back to default if there's an exception
      return DEFAULT_PRODUCT_CONTEXT;
    }
  }
  
  /**
   * Get the tone of voice for translations with improved formatting
   * @param selectedTeamId The team ID to filter by
   * @returns The tone of voice string with clear section formatting
   */
  private async getToneOfVoice(selectedTeamId?: string): Promise<string> {
    try {
      if (!selectedTeamId) return DEFAULT_TONE_OF_VOICE;
      // Fetch the brand tone from the active team
      const { data: teamData, error: teamError } = await translationSupabase
        .from('Teams')
        .select('tov_tone')
        .eq('id', selectedTeamId)
        .single();
      let formattedTone = "";
      let brandTone = "";
      if (!teamError && teamData?.tov_tone && teamData.tov_tone.trim()) {
        brandTone = teamData.tov_tone.trim();
        formattedTone += `## Brand tone\n${brandTone}\n\n`;
      }
      // Fetch all tone sections
      const { data: toneSections, error: sectionsError } = await translationSupabase
        .from('ToneSections')
        .select('*')
        .eq('team_id', selectedTeamId);
      if (sectionsError || !toneSections || toneSections.length === 0) {
        // Fall back to default if there's an error or no sections
        if (!brandTone) {
          return DEFAULT_TONE_OF_VOICE;
        } else {
          return formattedTone.trim();
        }
      }
      // Add each section with its rules
      toneSections.forEach(section => {
        // Skip sections with empty titles
        if (section.title && section.title.trim()) {
          formattedTone += `## ${section.title.trim()}\n`;
          // Only add description if it exists and isn't empty
          if (section.description && section.description.trim()) {
            formattedTone += `${section.description.trim()}\n\n`;
          }
          // Only add rules if they exist and aren't empty
          if (section.rules && section.rules.length > 0) {
            let hasRules = false;
            section.rules.forEach((rule: string) => {
              if (rule && rule.trim()) {
                formattedTone += `- ${rule.trim()}\n`;
                hasRules = true;
              }
            });
            // Only add extra newline if we actually added rules
            if (hasRules) {
              formattedTone += '\n';
            }
          }
        }
      });
      return formattedTone.trim();
    } catch (error) {
      // Fall back to default if there's an exception
      return DEFAULT_TONE_OF_VOICE;
    }
  }
  
  /**
   * Get the translation prompt for the system message
   * @param sourceLanguage The source language code
   * @param targetLanguage The target language code
   * @returns The system prompt for translation
   */
  private async getTranslationPrompt(sourceLanguage: string, targetLanguage: string): Promise<string> {
    const config: PromptConfig = {
      task: `Translate the following text from ${sourceLanguage} to ${targetLanguage}.`,
      guidelines: TRANSLATION_GUIDELINES,
      placeholders: DETAILED_PLACEHOLDERS,
      outputFormat: SINGLE_TRANSLATION_OUTPUT_FORMAT,
      sourceLanguage,
      targetLanguage
    };
    
    return this.buildPrompt(config);
  }
  
  /**
   * Get the batch translation prompt for the system message
   * @param sourceLanguage The source language code
   * @param targetLanguage The target language code
   * @returns The system prompt for batch translation
   */
  private async getBatchTranslationPrompt(sourceLanguage: string, targetLanguage: string): Promise<string> {
    const config: PromptConfig = {
      task: `Translate the following JSON array from ${sourceLanguage} to ${targetLanguage}.
You will receive an array of objects, each with a "key" and "value" property.`,
      guidelines: BATCH_TRANSLATION_GUIDELINES,
      placeholders: DETAILED_PLACEHOLDERS,
      outputFormat: BATCH_TRANSLATION_OUTPUT_FORMAT,
      sourceLanguage,
      targetLanguage
    };
    
    return this.buildPrompt(config);
  }
  
  /**
   * Centralized prompt builder function
   * @param config The prompt configuration
   * @returns The complete prompt string
   */
  private async buildPrompt(config: PromptConfig): Promise<string> {
    const persona = AI_TRANSLATOR_PERSONA;
    
    const productContext = await this.getProductContext();
    const tone = await this.getToneOfVoice();
    
    const sections: PromptSection[] = [
      { title: 'Your persona', content: persona },
      { title: 'Task', content: config.task },
      { title: 'Product Context', content: productContext },
      { title: 'Tone of voice', content: tone },
      { title: 'Important guidelines', content: config.guidelines },
      { title: 'Placeholders', content: config.placeholders },
      { title: 'Expected output format', content: config.outputFormat }
    ];
    
    let prompt = '';
    sections.forEach(section => {
      prompt += `# ${section.title}\n${section.content}\n\n`;
    });
    
    prompt += `Source language: ${config.sourceLanguage}\nTarget language: ${config.targetLanguage}`;
    
    return prompt;
  }
}

export const aiTranslationService = AITranslationService.getInstance(); 